import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, Clock,
  Search, ChevronRight, FileText, Wallet, Settings, Users
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";

const HEALTH_STYLES = {
  healthy: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Healthy", icon: TrendingUp },
  caution: { bg: "bg-amber-100", text: "text-amber-700", label: "Caution", icon: AlertTriangle },
  near_breakeven: { bg: "bg-orange-100", text: "text-orange-700", label: "Near B/E", icon: TrendingDown },
  loss: { bg: "bg-red-100", text: "text-red-700", label: "Loss", icon: TrendingDown }
};

function formatCurrency(value) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  }).format(value);
}

function formatCompact(value) {
  if (value === null || value === undefined) return "—";
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercent(value) {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(1)}%`;
}

export default function ProjectCostingView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ programs: [], totals: {} });
  
  // Filters
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState("all");
  const [hasUnpaid, setHasUnpaid] = useState(false);
  const [hasMissingRates, setHasMissingRates] = useState(false);
  
  // Bulk Rates Dialog
  const [showRatesDialog, setShowRatesDialog] = useState(false);
  const [resourcesWithoutRates, setResourcesWithoutRates] = useState([]);
  const [bulkRates, setBulkRates] = useState({});
  const [savingRates, setSavingRates] = useState(false);
  const [existingRates, setExistingRates] = useState([]);

  const isAdmin = user?.role === "COO" || user?.role === "Admin";

  useEffect(() => {
    fetchData();
  }, [healthFilter, hasUnpaid, hasMissingRates]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (healthFilter !== "all") params.append("health_filter", healthFilter);
      if (hasUnpaid) params.append("has_unpaid", "true");
      if (hasMissingRates) params.append("has_missing_rates", "true");
      
      const res = await apiClient.get(`/costing/portfolio?${params}`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load costing data");
    } finally {
      setLoading(false);
    }
  };
  
  const openRatesDialog = async () => {
    try {
      const [withoutRatesRes, existingRatesRes] = await Promise.all([
        apiClient.get("/costing/resources-without-rates"),
        apiClient.get("/costing/resource-rates")
      ]);
      setResourcesWithoutRates(withoutRatesRes.data.resources || []);
      setExistingRates(existingRatesRes.data || []);
      
      // Initialize bulk rates form with empty values
      const initial = {};
      (withoutRatesRes.data.resources || []).forEach(r => {
        initial[r.id] = "";
      });
      setBulkRates(initial);
      setShowRatesDialog(true);
    } catch (err) {
      toast.error("Failed to load resources");
    }
  };
  
  const handleSaveBulkRates = async () => {
    // Filter out empty rates
    const rates = Object.entries(bulkRates)
      .filter(([_, rate]) => rate && parseFloat(rate) > 0)
      .map(([resource_id, hourly_rate]) => ({
        resource_id,
        hourly_rate: parseFloat(hourly_rate),
        rate_type: "hourly",
        currency: "USD",
        cost_category: "labor_internal"
      }));
    
    if (rates.length === 0) {
      toast.error("Please enter at least one rate");
      return;
    }
    
    setSavingRates(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await apiClient.post("/costing/resource-rates/bulk", {
        rates,
        effective_start_date: today
      });
      
      toast.success(`Saved ${res.data.created_count} new rates, updated ${res.data.updated_count}`);
      setShowRatesDialog(false);
      fetchData(); // Refresh to update missing rates count
    } catch (err) {
      toast.error("Failed to save rates");
    } finally {
      setSavingRates(false);
    }
  };

  const filteredPrograms = data.programs.filter(p => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.program_name?.toLowerCase().includes(searchLower) ||
      p.client_name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-costing-view">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Project Costing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time profitability tracking across all programs
          </p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={openRatesDialog} data-testid="manage-rates-btn">
            <Settings className="w-4 h-4 mr-2" />
            Manage Rates
          </Button>
        )}
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Programs</p>
                <p className="text-lg font-bold">{data.totals.total_programs || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Healthy</p>
                <p className="text-lg font-bold text-emerald-600">{data.totals.programs_healthy || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">At Risk</p>
                <p className="text-lg font-bold text-amber-600">{data.totals.programs_at_risk || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Wallet className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Unpaid</p>
                <p className="text-lg font-bold text-red-600">{formatCompact(data.totals.unpaid_vendor_costs)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            
            <Select value={healthFilter} onValueChange={setHealthFilter}>
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="healthy">Healthy</SelectItem>
                <SelectItem value="caution">Caution</SelectItem>
                <SelectItem value="near_breakeven">Near B/E</SelectItem>
                <SelectItem value="loss">Loss</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant={hasUnpaid ? "default" : "outline"}
              size="sm"
              onClick={() => setHasUnpaid(!hasUnpaid)}
              className="h-8 text-xs"
            >
              Unpaid
            </Button>
            
            <Button
              variant={hasMissingRates ? "default" : "outline"}
              size="sm"
              onClick={() => setHasMissingRates(!hasMissingRates)}
              className="h-8 text-xs"
            >
              Missing Rates
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Programs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs font-semibold">Program</TableHead>
                {isAdmin && <TableHead className="text-xs font-semibold text-right">Contract</TableHead>}
                <TableHead className="text-xs font-semibold text-right">Costs</TableHead>
                {isAdmin && <TableHead className="text-xs font-semibold text-right">Profit</TableHead>}
                {isAdmin && <TableHead className="text-xs font-semibold text-right">Margin</TableHead>}
                <TableHead className="text-xs font-semibold text-center">Status</TableHead>
                <TableHead className="text-xs font-semibold text-right">Unpaid</TableHead>
                {isAdmin && <TableHead className="text-xs font-semibold text-right">Runway</TableHead>}
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPrograms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 5} className="text-center py-8 text-muted-foreground text-sm">
                    No programs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrograms.map(program => {
                  const healthStyle = HEALTH_STYLES[program.margin_health] || HEALTH_STYLES.healthy;
                  const HealthIcon = healthStyle.icon;
                  
                  return (
                    <TableRow 
                      key={program.program_id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => navigate(`/programs/${program.program_id}?tab=costing`)}
                    >
                      <TableCell className="py-2">
                        <div>
                          <span className="text-sm font-medium">{program.program_name}</span>
                          {program.client_name && (
                            <p className="text-[10px] text-muted-foreground">{program.client_name}</p>
                          )}
                          {program.has_missing_rates && (
                            <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300 mt-0.5">
                              Missing Rates
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right text-sm">
                          {formatCompact(program.contract_value)}
                        </TableCell>
                      )}
                      <TableCell className="text-right text-sm">
                        {formatCompact(program.total_cost)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className={`text-right text-sm ${program.gross_profit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formatCompact(program.gross_profit)}
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell className="text-right text-sm font-medium">
                          {formatPercent(program.gross_margin_percent)}
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <Badge className={`${healthStyle.bg} ${healthStyle.text} text-[10px] px-1.5 py-0.5`}>
                          {healthStyle.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {program.unpaid_vendor_costs > 0 ? (
                          <span className="text-red-600">
                            {formatCompact(program.unpaid_vendor_costs)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right text-sm">
                          {program.runway_to_target !== null ? (
                            <span className={program.runway_to_target < 14 ? 'text-amber-600' : 'text-muted-foreground'}>
                              {program.runway_to_target}d
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Bulk Resource Rates Dialog */}
      <Dialog open={showRatesDialog} onOpenChange={setShowRatesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Resource Rates
            </DialogTitle>
            <DialogDescription>
              Set hourly rates for artists and PMs. Resources without rates show "Missing Rate" warnings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Resources Without Rates */}
            {resourcesWithoutRates.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Resources Without Rates ({resourcesWithoutRates.length})
                </h4>
                <div className="space-y-2">
                  {resourcesWithoutRates.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-4 p-3 bg-amber-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{r.display_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.resource_type === "artist" ? "Artist" : "PM"} • {r.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">$/hr</span>
                        <Input
                          type="number"
                          placeholder="0"
                          className="w-24 h-8"
                          value={bulkRates[r.id] || ""}
                          onChange={e => setBulkRates({ ...bulkRates, [r.id]: e.target.value })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {resourcesWithoutRates.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                <p>All resources have rates configured!</p>
              </div>
            )}
            
            {/* Existing Rates Summary */}
            {existingRates.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3">Existing Rates ({existingRates.length})</h4>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Resource</TableHead>
                        <TableHead className="text-xs text-right">Rate</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {existingRates.map(rate => (
                        <TableRow key={rate.id}>
                          <TableCell className="text-sm py-2">{rate.resource_name || rate.resource_id}</TableCell>
                          <TableCell className="text-sm text-right py-2">${rate.hourly_rate}/hr</TableCell>
                          <TableCell className="text-xs text-muted-foreground py-2">{rate.cost_category}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRatesDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveBulkRates} 
              disabled={savingRates || Object.values(bulkRates).filter(v => v && parseFloat(v) > 0).length === 0}
            >
              {savingRates ? "Saving..." : "Save Rates"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
